import { doNothing, replaceFirst } from "@greeter/util";
import { useIonAlert, useIonToast, useIonViewWillEnter } from "@ionic/react";
import React, { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { productKeys } from "@greeter-venue/api/QueryKeys";
import { Routes } from "@greeter-venue/utility/Routes";
import { ProductPage } from "./ProductPage";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { Product, SpecialPricePeriod } from "@greeter/core";
import { useProductQuery } from "@greeter-venue/api/Hooks";
import { useLoading } from "@greeter/loading";
import { OnCreateArgs, OnUpdateArgs } from "./ProductPage";
import { CreateProductRequest, UpdateProductRequest } from "@greeter/venue-api";
import { ApiVersions } from "@greeter/api";
import { useGreeterVenueApi } from "@greeter/matter";

type Params = {
  id?: string;
};

export type ProductPageApiHandlerProps = {
  id?: string;
  onProductCreated?: (productId: string) => void;
  onProductUpdated?: (productId: string) => void;
  onProductDeleted?: () => void;
};

export function ProductPageApiHandler({
  onProductCreated,
  onProductDeleted,
  onProductUpdated,
}: ProductPageApiHandlerProps) {
  const routeMatch = useRouteMatch<Params>();
  const id = routeMatch?.params.id;
  const history = useHistory();

  const { api: venueApi } = useGreeterVenueApi();
  const queryClient = useQueryClient();

  const { waitFor } = useLoading();
  const [showToast] = useIonToast();
  const [showAlert] = useIonAlert();

  const handleError = () => {
    showToast("Et eller andet gik galt. Prøv igen senere.", 1000);
  };

  const refreshCache = useCallback(
    (productId: string) => {
      queryClient.invalidateQueries({ queryKey: productKeys.all() });
      queryClient.invalidateQueries({
        queryKey: productKeys.detail(productId),
      });
    },
    [queryClient]
  );

  const productQuery = useProductQuery(id);

  const updateCache = useCallback(
    (p: Product) => {
      if (!p) return;
      queryClient.setQueryData(productKeys.detail(p.id!), p);
      queryClient.setQueryData(
        productKeys.all(),
        (prev: Product[] | undefined) => {
          return prev?.map((pp) => (pp.id === p.id ? p : pp)) ?? [];
        }
      );
    },
    [queryClient]
  );

  const createMutation = useMutation({
    mutationFn(args: CreateProductRequest) {
      return venueApi.createProduct(args, { apiVersion: ApiVersions.V1 });
    },
    onError: handleError,
    onSuccess: (p: Product) => {
      updateCache(p);
      if (onProductCreated) {
        onProductCreated(p.id);
      }
    },
  });
  const updateMutation = useMutation({
    async mutationFn(args: UpdateProductRequest) {
      return await venueApi.updateProduct(args, { apiVersion: ApiVersions.V1 });
    },
    onError: handleError,
    onSuccess: (p: Product) => {
      updateCache(p);
      if (onProductUpdated) {
        onProductUpdated(p.id);
      }
    },
  });
  const deleteMutation = useMutation({
    mutationFn(args: string) {
      return venueApi.deleteProduct(args, { apiVersion: ApiVersions.V1 });
    },
    onError: handleError,
    onSuccess: () => {
      queryClient.setQueryData(
        productKeys.all(),
        (prev: Product[] | undefined) => {
          return prev?.filter((p) => p.id !== id) ?? [];
        }
      );
      if (id) {
        queryClient.removeQueries({ queryKey: productKeys.detail(id) });
      }

      if (onProductDeleted) {
        onProductDeleted();
      }
      showToast("Produktet er slettet", 1000);
      // Note: We have to set a timeout for now. Otherwise we might end in a "dead" state where you're unable to
      //       route away from ProductPage. It's seemingly a race condition in the history object
      //       that is causing the mismatch. It is as if react-router-dom loses its "grip".
      //       Therefore we wait for the routing stuff to happen and den move on to the venue page.
      //       It is 2s atm. just to be on the safe side. 1s seemed to work, but we'd hade for venue admins
      //       to get stuck just because they wanted to delete a product.
      setTimeout(
        () => history.push(Routes.mainGroup.venueTab.root.route()),
        2000
      );
    },
  });
  const uploadMutation = useMutation({
    mutationFn: venueApi.uploadAsset.bind(venueApi),
    onError: handleError,
  });

  const saveSpecialPricePeriodsMutation = useMutation({
    mutationFn: venueApi.saveSpecialPricePeriods.bind(venueApi),
  });

  const deleteSpecialPricePeriodsMutation = useMutation({
    mutationFn: venueApi.deleteSpecialPricePeriods.bind(venueApi),
  });

  const handleCreate = useCallback(
    async function onCreate({
      createArgs: product,
      cover: image,
      specialPricePeriods,
    }: OnCreateArgs) {
      console.log(`[${onCreate.name}] called`, product, specialPricePeriods);
      const asset = await waitFor(
        uploadMutation.mutateAsync({ data: image }),
        "Uploader billede"
      );
      product.coverAsset = { id: asset.id };
      product.subTitle = product.subTitle ?? product.title;
      const createdProduct = await waitFor(
        createMutation.mutateAsync(product),
        "Opretter produkt"
      );
      if (specialPricePeriods.length > 0) {
        await waitFor(
          saveSpecialPricePeriodsMutation.mutateAsync(
            specialPricePeriods.map((spp) => ({
              ...spp,
              productId: createdProduct.id,
            }))
          ),
          "Gemmer specielle priser"
        );
      }
      history.push(
        Routes.mainGroup.productCreated.route({ id: createdProduct.id! })
      );
      showToast(`${product.title} er blevet oprettet!`, 1000);
    },
    [createMutation, history, showToast, uploadMutation]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      showAlert("Er du sikker på at du vil slette produktet?", [
        {
          text: "Ja",
          handler: async () => {
            await waitFor(deleteMutation.mutateAsync(id), "Sletter produkt");
            history.push(Routes.mainGroup.venueTab.root.route());
          },
        },
        { text: "Nej" },
      ]);
    },
    [deleteMutation, showAlert]
  );

  const handleUpdate = useCallback(
    async function onUpdate({
      product: updateProductArgs,
      newCover: newImage,
      specialPricePeriods,
      specialPricePeriodsForDeletion,
    }: OnUpdateArgs) {
      try {
        if (newImage) {
          const asset = await waitFor(
            uploadMutation.mutateAsync({ data: newImage }),
            "Uploader nyt billede"
          );
          updateProductArgs.coverId = asset.id;
        }
        updateProductArgs.subTitle =
          updateProductArgs.subTitle ?? updateProductArgs.title;
        await waitFor(
          Promise.all([
            updateMutation.mutateAsync(updateProductArgs),
            specialPricePeriodsForDeletion.length > 0
              ? deleteSpecialPricePeriodsMutation.mutateAsync(
                  specialPricePeriodsForDeletion
                )
              : Promise.resolve(),
            specialPricePeriods.length > 0
              ? saveSpecialPricePeriodsMutation.mutateAsync(specialPricePeriods)
              : Promise.resolve(),
          ]),
          "Opdaterer produktet"
        );
        showToast("Produktet er opdateret!", 1000);
      } catch {
        showToast("Produktet kunne ikke opdateres", 1000);
      }
    },
    [showToast, updateMutation, uploadMutation]
  );

  const specialPricePeriodsQuery = useQuery({
    queryKey: ["specialPricePeriods", productQuery.data?.id],
    async queryFn() {
      if (productQuery.data?.id) {
        return await venueApi.fetchSpecialPricePeriods(productQuery.data.id);
      }
    },
    enabled: !!productQuery?.data,
    staleTime: Infinity,
  });

  useIonViewWillEnter(() => {
    specialPricePeriodsQuery.refetch();
  }, [productQuery.data]);

  const onCreate = useCallback(
    async (args: OnCreateArgs) => {
      await handleCreate(args);
      await specialPricePeriodsQuery.refetch();
    },
    [handleCreate, specialPricePeriodsQuery]
  );

  const onUpdate = useCallback(
    async (args: OnUpdateArgs) => {
      await handleUpdate(args);
      await specialPricePeriodsQuery.refetch();
    },
    [handleUpdate, specialPricePeriodsQuery]
  );

  return (
    <ProductPage
      // Lesson learned: Watch the fuck out for the key prop.
      // It caused "laggy" page changes which was causing rerouting issues.
      product={productQuery.data}
      onCreate={onCreate}
      onDelete={handleDelete}
      onUpdate={onUpdate}
      specialPricePeriods={specialPricePeriodsQuery.data}
    />
  );
}
